import React from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "components/common/Modal";
import { dateLocale } from "i18n";

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleTimeString(dateLocale("en-GB"), {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const ImpactHistoryModal = ({ show, onClose, impacts, selectedPlayerName }) => {
  const { t } = useTranslation();
  const title = selectedPlayerName
    ? t("session.impactHistory.playerTitle", { name: selectedPlayerName })
    : t("session.impactHistory.title");

  return (
    <Modal show={show} onClose={onClose} maxWidth="600px">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        <h2 className="text-base font-semibold text-white">{title}</h2>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition duration-150 hover:bg-white/10 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div className="max-h-[400px] overflow-y-auto">
        {impacts.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-[#6B7280]">
              {t("session.impactHistory.empty")}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#F3F4F6] bg-[#F9FAFB]">
                <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                  {t("session.impactHistory.columns.time")}
                </th>
                {!selectedPlayerName && (
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                    {t("session.impactHistory.columns.player")}
                  </th>
                )}
                <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                  {t("session.impactHistory.columns.linear")}
                </th>
                <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">
                  {t("session.impactHistory.columns.rotational")}
                </th>
              </tr>
            </thead>
            <tbody>
              {impacts.map((impact, index) => (
                <tr
                  key={index}
                  className="border-b border-[#F3F4F6] transition duration-150 last:border-b-0 hover:bg-[#FAFAFA]"
                >
                  <td className="px-4 py-2.5 text-[13px] text-[#374151]">
                    {formatTimestamp(impact.timestamp)}
                  </td>
                  {!selectedPlayerName && (
                    <td className="px-4 py-2.5 text-[13px] font-medium text-[#111827]">
                      {impact.playerName}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-right text-[13px] font-semibold text-[#111827]">
                    {impact.linear_g != null
                      ? Number(impact.linear_g).toFixed(1)
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right text-[13px] text-[#6B7280]">
                    {impact.rotational != null
                      ? Number(impact.rotational).toFixed(1)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-[#F3F4F6] px-6 py-3">
        <span className="text-xs text-[#9CA3AF]">
          {t("session.impactHistory.impactCount", { count: impacts.length })}
        </span>
        <button
          onClick={onClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.close")}
        </button>
      </div>
    </Modal>
  );
};

export default ImpactHistoryModal;
